import { Typography, Row, Col, Progress, message } from 'antd';
import React, { useState } from 'react'
import DatabaseSelector from './DatabaseSelector';
import SequenceInput from './SequenceInput';
import FileUpload from './FileUpload';
import { RefLanguage } from '../hooks/LanguageHooks';
import * as con from "../Constants"
import { RefIsFinalSequenceReadyForAnnotation, RefTextSequencesStatus, RefUploadedFileSequencesStatus } from '../hooks/SequenceHooks';
import { isNullOrUndefinedOrBlank, sleep } from '../utils/generalFunctions';
import { TestDataButton } from './Buttons';
import AnnotationService from './AnnotationService';
// import { StateStoredValue } from '../hooks/StoreHooks';
import { RefStoredValue, StateStoredValue } from '../hooks/StoreHooks';
import { setStoredValue } from '../store/actions';
import { CustomRunButton } from '../elements/CustomButtons';
import SequenceBeingAnnotatedComponent from './SequenceBeingAnnotatedComponent';
import { pushNewAnnotationResult, resetInputSequences } from '../store/sequencesActions';
import { RefSetCurrentPage } from '../hooks/NavigationHooks';
import ServerDown from './ServerDown';
import { RefLargeObjectValue } from '../hooks/LargeObjectsHooks';

const { Title, Text } = Typography;



function AnnotateNewSequenceTool() {


    const i18n = RefLanguage()

    // Set the current page
    const setPage = RefSetCurrentPage()

    // Server Status
    const serverStatus = RefStoredValue(con.SERVER_STATUS)

    // Ref sequence status
    const [textInputSequenceStatus, textInputSequenceMessage, textInputSequenceMessageContext] = RefTextSequencesStatus()
    const [uploadedFileSequenceStatus, uploadedFileSequenceMessage, uploadedFileSequenceMessageContext] = RefUploadedFileSequencesStatus()

    // Get stored values for input sequence and uploaded file
    const uploadedFile = RefStoredValue(con.UPLOADED_FILE_NAME)
    const finalInputSequence = RefLargeObjectValue(con.FINAL_INPUT_SEQUENCE)
    const isFinalSequenceValid = RefIsFinalSequenceReadyForAnnotation()

    // Selected database
    const selectedDatabase = RefStoredValue(con.SELECTED_DATABASE);

    // Annotation status
    const [annotationStatus, setAnnotationStatus] = useState(con.STATUS_NOT_STARTED);
    const [annotationMessage, setAnnotationMessage] = StateStoredValue(con.PROCESSING_SEQUENCE_MESSAGE);

    // Variable for user experience
    const [isFinished, setIsFinished] = useState(false)


    // Function to send file to server and annotate it
    const sendFileToServer = async (method) => {

        // Starts process
        setAnnotationStatus(con.STATUS_NOT_STARTED);
        setIsFinished(false)




        // Check if either text input or file upload is valid
        if (textInputSequenceStatus !== con.STATUS_OK && uploadedFileSequenceStatus !== con.STATUS_OK) {
            message.error(i18n.t('noValidSequences'));
            return;
        }

        // Check if database is selected
        if (isNullOrUndefinedOrBlank(selectedDatabase)) {
            message.error(i18n.t('databaseNotSelected'));
            return;
        }

        setAnnotationStatus(con.STATUS_PROCESSING);

        try {
            // Build a File from the processed final sequence, regardless of origin
            if (!finalInputSequence) {
                throw new Error(i18n.t('noValidSequences'));
            }

            // Standardize upload filename to .fasta for consistency
            // If a name is available, strip any existing extension and append .fasta; otherwise use a default
            const originalName = (typeof uploadedFile === 'string' && uploadedFile) ? uploadedFile : 'sequence';
            const lastDotIndex = originalName.lastIndexOf('.');
            const baseName = lastDotIndex > 0 ? originalName.substring(0, lastDotIndex) : originalName;
            const safeName = `${baseName}.fasta`;
            const blob = new Blob([finalInputSequence], { type: 'text/plain' });
            const file = new File([blob], safeName, { type: 'text/plain' });

            // Run annotation with service
            await AnnotationService.runAnnotation(
                file,
                method,
                selectedDatabase,
                // Progress callback
                (progress) => {
                    setAnnotationMessage(progress);
                },
                // Success callback
                async (results) => {
                    message.success(i18n.t('annotationComplete'));

                    // Saves new result
                    pushNewAnnotationResult(results);

                    // Reset Interface
                    resetInputSequences()

                    // Navigates to Results
                    setIsFinished(true);

                    setStoredValue(con.ANNOTATION_RESULT_DATA, results.results);

                    // Usability
                    await sleep(2000);

                    setPage(con.PAGE_ANNOTATION_RESULTS);

                    setAnnotationStatus(con.STATUS_OK);

                },
                // Error callback
                (error) => {
                    setAnnotationStatus(con.STATUS_ERROR);
                    message.error(error);
                }
            );
        } catch (error) {
            message.error(error.message || i18n.t('annotationFailed'));
            setAnnotationStatus(con.STATUS_ERROR);

        }
    }



    return (
        <div>


            {serverStatus === con.STATUS_ERROR
                ? <ServerDown />
                : <div />}


            {annotationStatus === con.STATUS_PROCESSING
                ? <SequenceBeingAnnotatedComponent isFinished={isFinished} annotationMessage={annotationMessage} />
                : <div />}


            <Row style={{ marginTop: "0.3vh", padding: "0 2vh" }}>
                <Col span={24} style={{ textAlign: 'center' }}>
                    <Typography >
                        <Title level={3} style={{ marginTop: "0.3vh", paddingTop: 0 }}>{i18n.t("mainTool")}</Title>
                    </Typography>
                </Col>
            </Row>


            <div style={{ marginBottom: "2vh" }}>
                <DatabaseSelector />
            </div>
            <div>
                <Row gutter={16}>
                    <Col span={19}>
                        <SequenceInput />
                    </Col>
                    <Col span={5}>
                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                            <FileUpload />
                            <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'center' }}>
                                <TestDataButton />
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>
            <div data-tour="validation-status">
                <div>
                    <Row gutter={16}>
                        <Col span={18}>
                            <Progress percent={textInputSequenceStatus === con.STATUS_IS_MISSING || textInputSequenceStatus === con.STATUS_PROCESSING ? 0 : 100} showInfo={false} strokeColor={{ '100%': textInputSequenceStatus === con.STATUS_OK ? 'var(--primary-color-1-t)' : 'var(--danger-color-1)' }} />
                            <div style={{ minHeight: "5vh", visibility: textInputSequenceStatus === con.STATUS_IS_MISSING || textInputSequenceStatus === con.STATUS_PROCESSING ? "hidden" : "visible" }}>
                                <Text>{isNullOrUndefinedOrBlank(textInputSequenceMessage) ? "" : i18n.t(textInputSequenceMessage, textInputSequenceMessageContext)}</Text>
                            </div>
                        </Col>
                        <Col span={6}>
                            <Progress percent={uploadedFileSequenceStatus === con.STATUS_IS_MISSING ? 0 : 100} showInfo={false} strokeColor={{ '100%': uploadedFileSequenceStatus === con.STATUS_OK ? 'var(--primary-color-1-t)' : 'var(--danger-color-1)' }} />
                            <div style={{ minHeight: "5vh", visibility: uploadedFileSequenceStatus === con.STATUS_IS_MISSING ? "hidden" : "visible" }}>
                                <Text>{isNullOrUndefinedOrBlank(uploadedFileSequenceMessage) ? "" : i18n.t(uploadedFileSequenceMessage, uploadedFileSequenceMessageContext)}</Text>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>
            <div >
                <Row>
                    <Col span={10} />
                    <Col span={4}></Col>
                    <Col span={10} />

                </Row>
            </div>
            <div data-tour="analysis-methods" style={{ marginTop: "1vh" }}>
                <Typography style={{ marginBottom: "1vh" }}>
                    <Title level={4}>{i18n.t("runTool")}</Title>
                </Typography>
                <Row gutter={[16, 16]}>
                    <Col span={4} />
                    <Col span={4}>
                        <CustomRunButton disabled={!isFinalSequenceValid} onClick={() => sendFileToServer(con.ANNOTATION_METHOD_BLAST)} >BLASTn</CustomRunButton>
                    </Col>
                    <Col span={4}>
                        <CustomRunButton disabled={!isFinalSequenceValid} onClick={() => sendFileToServer(con.ANNOTATION_METHOD_MEGABLAST)} >MegaBLAST</CustomRunButton>
                    </Col>
                    <Col span={4}>
                        <CustomRunButton disabled={!isFinalSequenceValid} onClick={() => sendFileToServer(con.ANNOTATION_METHOD_VSEARCH)} >VSEARCH</CustomRunButton>
                    </Col>
                    <Col span={4}>
                        <CustomRunButton disabled={!isFinalSequenceValid} onClick={() => sendFileToServer(con.ANNOTATION_METHOD_EXACT)} width={"120%"} >{i18n.t("exactMatch")}</CustomRunButton>
                    </Col>
                    <Col span={4} />
                </Row>
            </div>
        </div>
    )
}

export default AnnotateNewSequenceTool
